import { CustomTypeCategory } from '../../../../services/CustomTypes'
import { CHALLENGE_ADMIN_STEP_GOAL, CHALLENGE_ADMIN_STEP_CONFIGURATION } from '../../ChallengeAdmin'
import { isSimpleDayType } from '../../../../common/helpers/challenges'

/**
 * Validation rules for consistency goal fields.
 * Centralized to reduce duplication and improve maintainability.
 */
export const getConsistencyGoalValidationRules = (
  formStep: number,
  activityType: CustomTypeCategory,
  goalType: string,
  isDefinedCustomActivity: boolean,
  simpleManualEntry: boolean,
) => {
  const isGoalStep = formStep === CHALLENGE_ADMIN_STEP_GOAL
  const isConsistencyGoal = goalType === 'consistentDays'

  return {
    // Daily goal validation
    dailyGoal: {
      min:
        isGoalStep &&
        ((!isDefinedCustomActivity &&
          activityType !== CustomTypeCategory.CUSTOM &&
          activityType !== CustomTypeCategory.ALL &&
          goalType === 'totalAmount') ||
          isConsistencyGoal)
          ? 1
          : undefined,
      max:
        isGoalStep &&
        ((!isDefinedCustomActivity &&
          activityType !== CustomTypeCategory.CUSTOM &&
          activityType !== CustomTypeCategory.ALL &&
          goalType === 'totalAmount') ||
          isConsistencyGoal)
          ? 100000
          : undefined,
      required:
        isGoalStep &&
        ((!isDefinedCustomActivity &&
          activityType !== CustomTypeCategory.CUSTOM &&
          activityType !== CustomTypeCategory.ALL &&
          goalType === 'totalAmount') ||
          isConsistencyGoal),
    },

    // Total goal (persistence goal) validation
    // Note: max is set dynamically based on days in the component
    persistenceGoal: {
      min: isGoalStep && isConsistencyGoal ? 1 : undefined,
      required: isGoalStep && isConsistencyGoal,
    },

    // Custom unit validation
    customUnit: {
      required:
        isGoalStep &&
        activityType === CustomTypeCategory.CUSTOM &&
        !simpleManualEntry &&
        ['totalAmount', 'consistentDays', 'collective'].includes(goalType),
    },

    // Custom type (unit selector) validation
    customType: {
      min: isGoalStep && activityType === CustomTypeCategory.ALL ? 1 : undefined,
      max: isGoalStep && activityType === CustomTypeCategory.ALL ? 1000 : undefined,
      required: isGoalStep && activityType === CustomTypeCategory.ALL,
    },

    // Index target validation
    indexTarget: {
      min: isGoalStep && activityType === CustomTypeCategory.ALL ? 1 : undefined,
      max: isGoalStep && activityType === CustomTypeCategory.ALL ? 1000 : undefined,
      required: isGoalStep && activityType === CustomTypeCategory.ALL,
    },
  }
}

/**
 * Check if daily goal field should be shown for consistency goals.
 */
export const shouldShowDailyGoal = (
  activityType: CustomTypeCategory,
  isDefinedCustomActivity: boolean,
  simpleManualEntry: boolean,
): boolean => {
  return (
    activityType !== CustomTypeCategory.ALL &&
    !isSimpleDayType(activityType) &&
    // If custom type and simple manual entry is enabled, daily goal is disabled
    (activityType !== CustomTypeCategory.CUSTOM || !simpleManualEntry)
  )
}

/**
 * Check if custom unit input should be shown.
 */
export const shouldShowCustomUnitInput = (
  activityType: CustomTypeCategory,
  simpleManualEntry: boolean,
  goalType: string,
): boolean => {
  return (
    activityType === CustomTypeCategory.CUSTOM &&
    // If consistency: Hide unit field if CUSTOM & SIMPLE
    !(goalType === 'consistentDays' && activityType === CustomTypeCategory.CUSTOM && simpleManualEntry)
  )
}

/**
 * Validation rules for total amount goal fields.
 * Centralized to reduce duplication and improve maintainability.
 */
export const getTotalAmountGoalValidationRules = (
  formStep: number,
  activityType: CustomTypeCategory,
  goalType: string,
  isDefinedCustomActivity: boolean,
  simpleManualEntry: boolean,
) => {
  const isGoalStep = formStep === CHALLENGE_ADMIN_STEP_GOAL
  const isTotalAmountGoal = goalType === 'totalAmount'

  return {
    // Total goal validation
    totalGoal: {
      min:
        isGoalStep && (activityType === CustomTypeCategory.CUSTOM || isDefinedCustomActivity) && isTotalAmountGoal
          ? 1
          : undefined,
      max: isGoalStep && isTotalAmountGoal ? 2000000000 : undefined,
      required:
        isGoalStep && (activityType === CustomTypeCategory.CUSTOM || isDefinedCustomActivity) && isTotalAmountGoal,
    },

    // Daily max validation
    dailyMax: {
      min: undefined, // Will be set dynamically based on dailyGoalEnabled
      max: undefined, // Will be set dynamically based on dailyGoalEnabled
    },

    // Custom unit validation
    customUnit: {
      required:
        isGoalStep &&
        activityType === CustomTypeCategory.CUSTOM &&
        (!simpleManualEntry || goalType === 'totalAmount') &&
        ['totalAmount', 'consistentDays', 'collective'].includes(goalType),
    },

    // Custom type (unit selector) validation
    customType: {
      min: isGoalStep && activityType === CustomTypeCategory.ALL ? 1 : undefined,
      max: isGoalStep && activityType === CustomTypeCategory.ALL ? 1000 : undefined,
      required: isGoalStep && activityType === CustomTypeCategory.ALL,
    },

    // Index target validation
    indexTarget: {
      min: isGoalStep && activityType === CustomTypeCategory.ALL ? 1 : undefined,
      max: isGoalStep && activityType === CustomTypeCategory.ALL ? 1000 : undefined,
      required: isGoalStep && activityType === CustomTypeCategory.ALL,
    },
  }
}

/**
 * Check if daily goal (daily max) is enabled for total amount goals.
 * Can be shown in both Goal step and Configuration step.
 */
export const isDailyMaxEnabled = (
  formStep: number,
  activityType: CustomTypeCategory,
  goalType: string,
  simpleManualEntry: boolean,
): boolean => {
  const isPoints = activityType === CustomTypeCategory.TOTAL_POINTS || activityType === CustomTypeCategory.POINTS
  const isActivityIndex = activityType === CustomTypeCategory.ALL || activityType === CustomTypeCategory.ACTIVITY_INDEX
  const isGoalOrConfigStep = formStep === CHALLENGE_ADMIN_STEP_GOAL || formStep === CHALLENGE_ADMIN_STEP_CONFIGURATION
  const isTotalAmountOrCollective = ['totalAmount', 'collective'].includes(goalType)
  return (
    !isPoints &&
    !isActivityIndex &&
    isGoalOrConfigStep &&
    isTotalAmountOrCollective &&
    // If custom type and simple manual entry is enabled, daily goal is disabled
    (activityType !== CustomTypeCategory.CUSTOM || !simpleManualEntry) &&
    !isSimpleDayType(activityType)
  )
}
